import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowUpDown,
  Building2,
  Calendar,
  ChevronDown,
  CircleDot,
  Briefcase,
  Folder,
  LayoutGrid,
  LayoutTemplate,
  List,
  Plus,
  Search,
  SignalHigh,
  SlidersHorizontal,
  Tag,
  UserCircle,
} from 'lucide-react';
import {
  useCreateTask,
  useEstimateTotals,
  useGeneralProject,
  useTasks,
} from '../../hooks/useTasks';
import { useDepartments } from '../../hooks/useDepartments';
import { useLabels } from '../../hooks/useLabels';
import { usePeople } from '../../hooks/usePeople';
import { usePermissions } from '../../hooks/usePermissions';
import { useProjects } from '../../hooks/useProjects';
import { useClients } from '../../hooks/useClients';
import { clientLabel } from '../../lib/clientLabel';
import { useTaskStatuses } from '../../hooks/useTaskStatuses';
import type { CreateTaskInput, TaskPriority } from '../../lib/tasksApi';
import { PRIORITY_OPTIONS, statusBadgeStyle } from './taskConstants';
import { Select } from '../ui/Select';
import { DateField } from '../ui/DateField';
import { MobileFilterSheet, type FilterDescriptor } from '../ui/MobileFilterSheet';
import { TaskForm } from './TaskForm';
import { KanbanBoard } from './KanbanBoard';
import { BulkActionsBar } from './BulkActionsBar';
import { LabelsManager } from './LabelsManager';
import { ApplyTemplateModal } from './ApplyTemplateModal';
import { LinearTaskList } from './linear/LinearTaskList';
import { ToastProvider } from './linear/Toast';
import { FadeIn, SkeletonTable } from '../ui/Skeleton';
import { useI18n } from '../../contexts/I18nContext';

// Both views group rows into sections — the board into columns, the list into
// collapsible status groups — so both need the whole set for a section to mean
// anything. Only the board said so; the list still paged at 25, which spread a
// company-wide backlog over six pages while every group header showed a count
// of the current page. An older task (and an overdue one is old by definition)
// then looked absent from /tasks rather than "on page 4", because nothing on
// screen suggested there was a page 4 until you scrolled past every group.
const PAGE_SIZE = 200;
// View mode persists across navigation (CLE-197) — independent of project scope.
const VIEW_KEY = 'cle197:tasksViewMode';

type ViewMode = 'list' | 'board';
type SortField = 'createdAt' | 'priority' | 'dueDate' | 'estimate';
type Order = 'asc' | 'desc';

function formatHours(hours: number): string {
  if (!hours) return '0h';
  return `${Number.isInteger(hours) ? hours : hours.toFixed(1)}h`;
}

function loadViewMode(): ViewMode {
  // Default to the Kanban board so the Tasks page matches the Leads page, where
  // cards are dragged between status columns (CLE-375). The list view is still
  // one click away and its choice is remembered per user. Only an explicit
  // 'list' preference opts out of the board default.
  try {
    const raw = localStorage.getItem(VIEW_KEY);
    return raw === 'list' ? 'list' : 'board';
  } catch {
    return 'board';
  }
}

export interface TasksViewProps {
  /** Locks the list/board/quick-add to a single project when set (CLE-197). */
  projectId?: string;
  /** Hide the cross-project filter dropdown (used inside a project's own tab). */
  hideProjectFilter?: boolean;
  /**
   * Locks the list to one client's tasks (all its projects plus its default
   * container) — the client page's Sarcini tab. Hides the client filter.
   */
  clientId?: string;
  /** Pre-selected (not locked) project in the create form, e.g. the client's container. */
  createProjectId?: string;
  /**
   * Personal-tasks mode ("Sarcini personale"): locked to the caller's hidden
   * personal project; hides the assignee/department controls since a personal
   * task always belongs to exactly one user and no department.
   */
  personal?: boolean;
  /**
   * Read the initial filter state from the URL query string. Every standalone
   * page wants this; a project's Tasks tab passes `false` when it is switching
   * projects, so one project's filters cannot follow you to the next.
   */
  seedFromUrl?: boolean;
}

/**
 * CLE-197 — the single shared Linear-style Tasks view.
 *
 * Renders list + board + filters + bulk actions + quick-add + drag-and-drop +
 * task detail + label management. Used both by the global Tasks page (no locked
 * project) and by a project's own Tasks tab (locked to `projectId`).
 *
 * Filter state lives in the URL query string so it survives reloads and deep
 * links. When `projectId` is set the filter is forced to that project and the
 * project filter dropdown is hidden.
 */
export function TasksView({
  projectId: lockedProjectId,
  hideProjectFilter = false,
  clientId: lockedClientId,
  createProjectId,
  personal = false,
  seedFromUrl = true,
}: TasksViewProps) {
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();

  // Seed filter state from the URL. Every surface does this, the project tab
  // included: it renders the same filter controls and mirrors them back into
  // the query string, so ignoring what the query string already said made a
  // link like ?dueTo=2026-09-07 show an empty filter field and an unfiltered
  // list — the tab and the global page then disagreed about which tasks exist.
  // The CLE-197 reset on project switch is now the caller's call (see
  // ProjectTasksTab) instead of a blanket "locked means no seed".
  // Used only inside useState initializers (run once on mount).
  const seed = (key: string) => (seedFromUrl ? searchParams.get(key) : null);

  // --- Filter state -------------------------------------------------------
  const [searchInput, setSearchInput] = useState(() => seed('q') ?? '');
  const [search, setSearch] = useState(() => seed('q') ?? '');
  // The URL `status` param holds the status SLUG (stable across renames).
  const [status, setStatus] = useState(() => seed('status') ?? '');
  const [priority, setPriority] = useState<TaskPriority | ''>(
    () => (seed('priority') as TaskPriority | null) ?? '',
  );
  const [labelId, setLabelId] = useState(() => seed('label') ?? '');
  const [departmentId, setDepartmentId] = useState(() => seed('department') ?? '');
  // Assignee filter holds a User id (the task.assigneeId FK points at User).
  const [assigneeId, setAssigneeId] = useState(() => seed('assignee') ?? '');
  // Deadline window (YYYY-MM-DD, inclusive on both ends) applied to task.dueDate.
  // Either end can stand alone: "from" only = everything due on/after that day.
  const [dueFrom, setDueFrom] = useState(() => seed('dueFrom') ?? '');
  const [dueTo, setDueTo] = useState(() => seed('dueTo') ?? '');
  // When locked to a project the filter is fixed; otherwise it is user-driven.
  const [projectFilter, setProjectFilter] = useState(() =>
    lockedProjectId ? '' : (searchParams.get('project') ?? ''),
  );
  const [clientFilter, setClientFilter] = useState(() =>
    lockedClientId || lockedProjectId ? '' : (searchParams.get('client') ?? ''),
  );
  const [sort, setSort] = useState<SortField>(
    () => (seed('sort') as SortField | null) ?? 'createdAt',
  );
  const [order, setOrder] = useState<Order>(() => (seed('order') as Order | null) ?? 'desc');
  const [page, setPage] = useState(() => {
    const p = Number(seed('page'));
    return Number.isFinite(p) && p > 0 ? p : 1;
  });

  const [creating, setCreating] = useState(false);
  const [view, setView] = useState<ViewMode>(() => loadViewMode());
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [managingLabels, setManagingLabels] = useState(false);
  // "Aplică șablon task-uri" — only offered inside a real project's Tasks tab
  // (a template instantiates onto one project, and personal tasks live on the
  // hidden per-user project where templates make no sense).
  const [applyingTemplate, setApplyingTemplate] = useState(false);
  const [templateNotice, setTemplateNotice] = useState<string | null>(null);

  // The project actually used for queries: the locked one wins.
  const effectiveProjectId = lockedProjectId ?? (projectFilter || undefined);
  const effectiveClientId = lockedProjectId
    ? undefined
    : (lockedClientId ?? (clientFilter || undefined));

  // The project name is only useful cross-project; once the scope is a single
  // project (locked tab or filter dropdown) repeating it on every task is noise.
  const showProjectName = !effectiveProjectId;

  // CLE-435: a user whose task scope is 'assigned' only ever sees their own
  // tasks (the backend forces assigneeId = self), so the "responsible" filter
  // and the sort control are both meaningless on that short personal queue —
  // hide them. Driven purely by "Domeniu sarcini" in Roluri și permisiuni, so
  // granting a role "Toate sarcinile" brings the full toolbar back with it.
  const { matrix, can } = usePermissions();
  // Personal mode is the same shape: the list only ever holds the caller's own
  // tasks, so the assignee/department/sort controls are equally meaningless.
  const scopedToOwnTasks = matrix?.tasks.scope === 'assigned' || personal;
  // Applying a template creates tasks, so it needs the same permission the
  // backend checks on POST /api/task-templates/:id/apply.
  const canApplyTemplate = Boolean(lockedProjectId) && !personal && can('tasks', 'canCreate');

  // The applied-template confirmation is transient; the created tasks are the
  // real feedback and they are already in the list below it.
  useEffect(() => {
    if (!templateNotice) return;
    const timer = window.setTimeout(() => setTemplateNotice(null), 6000);
    return () => window.clearTimeout(timer);
  }, [templateNotice]);

  // Persist the view mode so it survives navigation between contexts.
  useEffect(() => {
    try {
      localStorage.setItem(VIEW_KEY, view);
    } catch {
      // Non-fatal: persistence is a convenience only.
    }
  }, [view]);

  // Debounce the full-text search input (FR-TASK: global debounced search).
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Mirror filter state into the URL query string (CLE-197). Project scope is
  // not written when locked — it lives in the route, not the query.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    const set = (key: string, value: string) => {
      if (value) next.set(key, value);
      else next.delete(key);
    };
    set('q', search);
    set('status', status);
    set('priority', priority);
    set('label', labelId);
    set('department', departmentId);
    set('assignee', assigneeId);
    set('dueFrom', dueFrom);
    set('dueTo', dueTo);
    set('sort', sort === 'createdAt' ? '' : sort);
    set('order', order === 'desc' ? '' : order);
    set('page', page > 1 ? String(page) : '');
    if (!lockedProjectId) set('project', projectFilter);
    if (!lockedProjectId && !lockedClientId) set('client', clientFilter);
    setSearchParams(next, { replace: true });
  }, [
    search,
    status,
    priority,
    labelId,
    departmentId,
    assigneeId,
    dueFrom,
    dueTo,
    projectFilter,
    clientFilter,
    sort,
    order,
    page,
    lockedProjectId,
    lockedClientId,
  ]);

  const { data: labels } = useLabels();
  const { data: projectsData } = useProjects({ limit: 200 });
  const { data: generalProject } = useGeneralProject();
  const showClientFilter = !lockedProjectId && !lockedClientId && !personal;
  const { data: clientsData } = useClients({ limit: 100, active: 'true', sort: 'name' });
  const clientOptions = useMemo(
    () => (clientsData?.data ?? []).map((c) => ({ value: c.id, label: clientLabel(c) })),
    [clientsData],
  );
  // The "Fără proiect" container is hidden from the projects list, so the
  // filter adds it on top: "show me the tasks filed under no project".
  const projects = useMemo(() => {
    const list = projectsData?.projects ?? [];
    return generalProject
      ? [{ id: generalProject.id, code: null, name: generalProject.name }, ...list]
      : list;
  }, [projectsData, generalProject]);
  const { statuses, bySlug, isLoading: statusesLoading } = useTaskStatuses();
  const { data: departments } = useDepartments();
  const { data: people } = usePeople({ active: 'true' });
  // Only people linked to a login User can own a task (task.assigneeId -> User),
  // so the assignee filter lists those and filters by their userId.
  const assigneeOptions = useMemo(
    () =>
      (people ?? [])
        .filter((p) => p.userId)
        .map((p) => ({ value: p.userId as string, label: p.fullName })),
    [people],
  );

  // Sanitize the status filter once statuses load: legacy uppercase enum
  // tokens (old bookmarks like ?status=IN_PROGRESS) normalize to the slug,
  // unknown tokens are silently dropped.
  useEffect(() => {
    if (statusesLoading || !status) return;
    const match = bySlug[status] ?? bySlug[status.toLowerCase()];
    if (!match) setStatus('');
    else if (match.slug !== status) setStatus(match.slug);
  }, [statusesLoading, status, bySlug]);

  const statusFilter = !statusesLoading && status && bySlug[status] ? status : undefined;

  // The narrowing filters shared by the list and the header totals (CLE-435),
  // so the totals recompute for exactly the rows the list shows.
  const taskFilters = {
    search: search || undefined,
    status: statusFilter,
    priority: priority || undefined,
    label: labelId || undefined,
    departmentId: departmentId || undefined,
    assigneeId: assigneeId || undefined,
    dueFrom: dueFrom || undefined,
    dueTo: dueTo || undefined,
    projectId: effectiveProjectId,
    clientId: effectiveClientId,
  };

  const { data: totals } = useEstimateTotals(taskFilters);

  const { data, isLoading, isError } = useTasks({
    ...taskFilters,
    sort,
    order,
    page: view === 'board' ? 1 : page,
    pageSize: PAGE_SIZE,
  });

  const create = useCreateTask();

  const tasks = data?.tasks ?? [];
  const pagination = data?.pagination;

  // CLE-184/CLE-274: open a task on its dedicated detail page via the canonical
  // project-independent route. Route by human identifier when present (nicer
  // URLs) and fall back to the id for tasks without one.
  //
  // Navigates in place. This used to open a new tab to protect the list's
  // filters and scroll position, which costs more than it saves: installed as
  // an app, on a phone or a desktop, every task opened that way is another
  // window to close. The detail page's back button already prefers
  // `history.back()` precisely so the browser restores the list where it was.
  const navigate = useNavigate();
  const openTask = useCallback(
    (id: string) => {
      const task = tasks.find((item) => item.id === id);
      if (!task) return;
      navigate(`/tasks/${encodeURIComponent(task.identifier ?? task.id)}`);
    },
    [tasks, navigate],
  );

  // Anchor for Shift-click range selection on the Linear list.
  const lastSelectedRef = useRef<string | null>(null);

  function toggleSelect(id: string, shift = false) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (shift && lastSelectedRef.current) {
        const ids = tasks.map((t) => t.id);
        const a = ids.indexOf(lastSelectedRef.current);
        const b = ids.indexOf(id);
        if (a !== -1 && b !== -1) {
          const [lo, hi] = a < b ? [a, b] : [b, a];
          for (let i = lo; i <= hi; i++) next.add(ids[i]);
          return next;
        }
      }
      if (next.has(id)) next.delete(id);
      else next.add(id);
      lastSelectedRef.current = id;
      return next;
    });
  }

  const hasFilters = Boolean(
    search ||
    status ||
    priority ||
    labelId ||
    departmentId ||
    assigneeId ||
    dueFrom ||
    dueTo ||
    projectFilter ||
    clientFilter,
  );

  // Desktop: search, project, assignee and the due window stay visible; the
  // rest sits under "Mai multe filtre". Open on load when one of them is set
  // (e.g. a deep link) so an active filter is never hidden.
  const moreFiltersActive = [status, priority, labelId, departmentId, clientFilter].filter(
    Boolean,
  ).length;
  const [showMoreFilters, setShowMoreFilters] = useState(() => moreFiltersActive > 0);

  function resetFilters() {
    setSearchInput('');
    setSearch('');
    setStatus('');
    setPriority('');
    setLabelId('');
    setDepartmentId('');
    setAssigneeId('');
    setDueFrom('');
    setDueTo('');
    if (!lockedProjectId) setProjectFilter('');
    if (!lockedClientId) setClientFilter('');
    setPage(1);
  }

  function onFilter<T>(setter: (v: T) => void) {
    return (v: T) => {
      setter(v);
      setPage(1);
    };
  }

  function handleCreate(input: CreateTaskInput) {
    create.mutate(input, { onSuccess: () => setCreating(false) });
  }

  // Clicking a label pill on a row filters the list by that label.
  function handleLabelFilter(label: { id: string }) {
    onFilter(setLabelId)(label.id);
  }

  const totalHours = totals?.total.hours ?? 0;
  const totalTasks = totals?.total.tasks ?? 0;

  const labelOptions = useMemo(() => labels ?? [], [labels]);

  // Mobile bottom-sheet filters (Linear-style). Mirrors the desktop controls
  // below one-to-one — same option mappings, same `onFilter` setters (page
  // reset included) and the same conditional composition per surface variant.
  const mobileFilters: FilterDescriptor[] = [
    {
      kind: 'select',
      id: 'status',
      label: t('tasks.filter.status'),
      icon: <CircleDot size={16} />,
      value: status,
      onChange: onFilter(setStatus),
      options: statuses.map((s) => ({ value: s.slug, label: s.name, color: s.color })),
      loading: statusesLoading,
      placeholder: t('tasks.allStatuses'),
    },
    {
      kind: 'select',
      id: 'priority',
      label: t('tasks.filter.priority'),
      icon: <SignalHigh size={16} />,
      value: priority,
      onChange: (v) => onFilter(setPriority)(v as TaskPriority | ''),
      options: PRIORITY_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
      placeholder: t('tasks.allPriorities'),
    },
    ...(showClientFilter
      ? [
          {
            kind: 'select',
            id: 'client',
            label: t('tasks.filter.client'),
            icon: <Briefcase size={16} />,
            value: clientFilter,
            onChange: onFilter(setClientFilter),
            options: clientOptions,
            placeholder: t('tasks.allClients'),
          } satisfies FilterDescriptor,
        ]
      : []),
    ...(!hideProjectFilter && !lockedProjectId
      ? [
          {
            kind: 'select',
            id: 'project',
            label: t('tasks.filter.project'),
            icon: <Folder size={16} />,
            value: projectFilter,
            onChange: onFilter(setProjectFilter),
            options: projects.map((p) => ({
              value: p.id,
              label: p.code ? `${p.code} · ${p.name}` : p.name,
            })),
            placeholder: t('tasks.allProjects'),
          } satisfies FilterDescriptor,
        ]
      : []),
    ...(!personal
      ? [
          {
            kind: 'select',
            id: 'department',
            label: t('tasks.filter.department'),
            icon: <Building2 size={16} />,
            value: departmentId,
            onChange: onFilter(setDepartmentId),
            options: (departments ?? []).map((d) => ({
              value: d.id,
              label: d.name,
              color: d.color,
            })),
            placeholder: t('tasks.allDepartments'),
          } satisfies FilterDescriptor,
        ]
      : []),
    ...(!scopedToOwnTasks
      ? [
          {
            kind: 'select',
            id: 'assignee',
            label: t('tasks.allAssignees'),
            icon: <UserCircle size={16} />,
            value: assigneeId,
            onChange: onFilter(setAssigneeId),
            options: assigneeOptions,
            placeholder: t('tasks.allAssignees'),
          } satisfies FilterDescriptor,
        ]
      : []),
    {
      kind: 'select',
      id: 'label',
      label: t('tasks.filter.labels'),
      icon: <Tag size={16} />,
      value: labelId,
      onChange: onFilter(setLabelId),
      options: labelOptions.map((l) => ({
        value: l.id,
        label: `${l.name} (${l.taskCount})`,
        color: l.color,
      })),
      placeholder: t('tasks.allLabels'),
    },
    {
      kind: 'date-range',
      id: 'due',
      label: t('mobileFilters.dueDate'),
      icon: <Calendar size={16} />,
      from: dueFrom,
      to: dueTo,
      onFromChange: (v) => onFilter(setDueFrom)(v),
      onToChange: (v) => onFilter(setDueTo)(v),
    },
    ...(!scopedToOwnTasks
      ? [
          {
            kind: 'sort',
            id: 'sort',
            label: t('tasks.sort.label'),
            icon: <ArrowUpDown size={16} />,
            value: `${sort}:${order}`,
            onChange: (v: string) => {
              const [s, o] = v.split(':');
              setSort(s as SortField);
              setOrder(o as Order);
            },
            options: [
              { value: 'createdAt:desc', label: t('tasks.sort.newest') },
              { value: 'createdAt:asc', label: t('tasks.sort.oldest') },
              { value: 'priority:desc', label: t('tasks.sort.priority') },
              { value: 'dueDate:asc', label: t('tasks.sort.dueDate') },
              { value: 'estimate:desc', label: t('tasks.sort.estimate') },
            ],
            defaultValue: 'createdAt:desc',
          } satisfies FilterDescriptor,
        ]
      : []),
  ];

  // Estimate totals by status (FR-TASK: hour totals per Kanban column). On the
  // personal page there's typically a single lone badge, so it sits inline
  // with the subtitle instead of wasting a whole row.
  const statusBadges =
    totals && totals.byStatus.length > 0
      ? totals.byStatus.map((row) => (
          <span
            key={row.statusId}
            className="rounded px-2 py-1 text-xs font-medium"
            style={statusBadgeStyle(row.statusColor)}
          >
            {t('tasks.statusBadge', {
              label: row.statusLabel,
              count: row.tasks,
              hours: formatHours(row.hours),
            })}
          </span>
        ))
      : null;

  return (
    <div className="w-full">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">
            {personal ? t('personalTasks.title') : t('tasks.title')}
          </h1>
          <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-gray-500">
            <span>
              {t('tasks.subtitle', { count: totalTasks, hours: formatHours(totalHours) })}
            </span>
            {personal && statusBadges}
          </p>
          {templateNotice && (
            <p className="mt-1 text-sm font-medium text-green-700" role="status">
              {templateNotice}
            </p>
          )}
        </div>
        {/* One row on phones: icon-only toggle + labels, with the primary
            "Sarcină nouă" stretching to fill; full text labels return at sm
            (same icon-only pattern as the calendar view switcher). */}
        <div className="flex flex-wrap items-center gap-2">
          {/* List / Board view toggle (FR-TASK #6). */}
          <div className="inline-flex shrink-0 rounded border border-gray-300 p-0.5">
            <button
              type="button"
              onClick={() => setView('list')}
              aria-pressed={view === 'list'}
              aria-label={t('tasks.view.list')}
              title={t('tasks.view.list')}
              className={`inline-flex items-center gap-1 rounded px-2.5 py-1.5 text-sm ${
                view === 'list' ? 'bg-gray-100 text-gray-900' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <List size={15} /> <span className="hidden sm:inline">{t('tasks.view.list')}</span>
            </button>
            <button
              type="button"
              onClick={() => setView('board')}
              aria-pressed={view === 'board'}
              aria-label={t('tasks.view.board')}
              title={t('tasks.view.board')}
              className={`inline-flex items-center gap-1 rounded px-2.5 py-1.5 text-sm ${
                view === 'board' ? 'bg-gray-100 text-gray-900' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <LayoutGrid size={15} />{' '}
              <span className="hidden sm:inline">{t('tasks.view.board')}</span>
            </button>
          </div>
          <button
            type="button"
            onClick={() => setManagingLabels(true)}
            aria-label={t('tasks.labels')}
            title={t('tasks.labels')}
            className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <Tag size={15} /> <span className="hidden sm:inline">{t('tasks.labels')}</span>
          </button>
          {canApplyTemplate && (
            <button
              type="button"
              onClick={() => setApplyingTemplate(true)}
              aria-label={t('tasks.applyTemplate.button')}
              title={t('tasks.applyTemplate.button')}
              className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <LayoutTemplate size={15} />{' '}
              <span className="hidden sm:inline">{t('tasks.applyTemplate.button')}</span>
            </button>
          )}
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="inline-flex flex-1 items-center justify-center gap-1.5 rounded bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 sm:flex-none"
          >
            <Plus size={16} /> {t('tasks.new')}
          </button>
        </div>
      </div>

      {/* Per-status totals keep their own row on the multi-status pages. */}
      {!personal && statusBadges && <div className="mt-4 flex flex-wrap gap-2">{statusBadges}</div>}

      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
        <div className="flex w-full items-end gap-2 sm:w-72">
          <div className="min-w-0 flex-1">
            <label className="ui-field-label">{t('tasks.filter.search')}</label>
            <div className="relative">
              <Search
                size={16}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={t('tasks.searchPlaceholder')}
                className="min-h-[44px] w-full rounded border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-brand-500 focus:outline-none sm:min-h-0"
              />
            </div>
          </div>
          {/* Mobile-only Linear-style filter sheet; desktop keeps the inline
              dropdowns below. */}
          <MobileFilterSheet
            className="sm:hidden"
            filters={mobileFilters}
            onClearAll={resetFilters}
          />
        </div>

        {/* Desktop inline filters — `sm:contents` keeps them as direct flex
            items of the original wrap container at >= sm, while a single
            `hidden` removes them all on phones. */}
        <div className="hidden sm:contents">
          {!hideProjectFilter && (
            <Select
              label={t('tasks.filter.project')}
              clearable
              searchable
              value={projectFilter}
              onChange={onFilter(setProjectFilter)}
              placeholder={t('tasks.allProjects')}
              options={projects.map((p) => ({
                value: p.id,
                label: p.code ? `${p.code} · ${p.name}` : p.name,
              }))}
              className="w-full sm:w-52"
            />
          )}
          {!scopedToOwnTasks && (
            <Select
              label={t('tasks.allAssignees')}
              clearable
              searchable
              value={assigneeId}
              onChange={onFilter(setAssigneeId)}
              placeholder={t('tasks.allAssignees')}
              options={assigneeOptions}
              className="w-full sm:w-48"
            />
          )}
          {/* Deadline window. `max`/`min` cross-wire the two so an inverted range
            can't be picked. */}
          <div className="w-full sm:w-40">
            <label className="ui-field-label" htmlFor="tasks-due-from">
              {t('tasks.filter.dueFrom')}
            </label>
            <DateField
              id="tasks-due-from"
              value={dueFrom}
              max={dueTo || undefined}
              onChange={(v) => onFilter(setDueFrom)(v)}
              className="w-full"
            />
          </div>

          <div className="w-full sm:w-40">
            <label className="ui-field-label" htmlFor="tasks-due-to">
              {t('tasks.filter.dueTo')}
            </label>
            <DateField
              id="tasks-due-to"
              value={dueTo}
              min={dueFrom || undefined}
              onChange={(v) => onFilter(setDueTo)(v)}
              className="w-full"
            />
          </div>

          <button
            type="button"
            onClick={() => setShowMoreFilters((v) => !v)}
            aria-expanded={showMoreFilters}
            className={`inline-flex h-[38px] items-center gap-2 rounded-lg border px-3 text-sm font-medium transition-colors ${
              showMoreFilters || moreFiltersActive
                ? 'border-brand-500 bg-brand-50 text-brand-700'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <SlidersHorizontal size={15} />
            {t('tasks.moreFilters')}
            {moreFiltersActive > 0 && (
              <span className="rounded-full bg-brand-600 px-1.5 text-xs leading-5 text-fixed-white">
                {moreFiltersActive}
              </span>
            )}
            <ChevronDown
              size={15}
              className={`transition-transform ${showMoreFilters ? 'rotate-180' : ''}`}
            />
          </button>

          {hasFilters && (
            <button
              type="button"
              onClick={resetFilters}
              className="pb-2.5 text-sm text-blue-600 underline"
            >
              {t('tasks.clearFilters')}
            </button>
          )}

          {showMoreFilters && (
            <div className="flex w-full flex-wrap items-end gap-3 rounded-xl border border-gray-200 bg-gray-50 p-3">
              <Select
                label={t('tasks.filter.status')}
                clearable
                value={status}
                onChange={onFilter(setStatus)}
                placeholder={t('tasks.allStatuses')}
                options={statuses.map((s) => ({ value: s.slug, label: s.name, color: s.color }))}
                loading={statusesLoading}
                className="w-full sm:w-44"
              />
              <Select
                label={t('tasks.filter.priority')}
                clearable
                value={priority}
                onChange={(v) => onFilter(setPriority)(v as TaskPriority | '')}
                placeholder={t('tasks.allPriorities')}
                options={PRIORITY_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                className="w-full sm:w-44"
              />
              {showClientFilter && (
                <Select
                  label={t('tasks.filter.client')}
                  clearable
                  searchable
                  value={clientFilter}
                  onChange={onFilter(setClientFilter)}
                  placeholder={t('tasks.allClients')}
                  options={clientOptions}
                  className="w-full sm:w-48"
                />
              )}
              {!personal && (
                <Select
                  label={t('tasks.filter.department')}
                  clearable
                  value={departmentId}
                  onChange={onFilter(setDepartmentId)}
                  placeholder={t('tasks.allDepartments')}
                  options={(departments ?? []).map((d) => ({
                    value: d.id,
                    label: d.name,
                    color: d.color,
                  }))}
                  className="w-full sm:w-44"
                />
              )}
              <Select
                label={t('tasks.filter.labels')}
                clearable
                value={labelId}
                onChange={onFilter(setLabelId)}
                placeholder={t('tasks.allLabels')}
                options={labelOptions.map((l) => ({
                  value: l.id,
                  label: `${l.name} (${l.taskCount})`,
                  color: l.color,
                }))}
                className="w-full sm:w-44"
              />
              {!scopedToOwnTasks && (
                <Select
                  label={t('tasks.sort.label')}
                  value={`${sort}:${order}`}
                  onChange={(v) => {
                    const [s, o] = v.split(':');
                    setSort(s as SortField);
                    setOrder(o as Order);
                  }}
                  options={[
                    { value: 'createdAt:desc', label: t('tasks.sort.newest') },
                    { value: 'createdAt:asc', label: t('tasks.sort.oldest') },
                    { value: 'priority:desc', label: t('tasks.sort.priority') },
                    { value: 'dueDate:asc', label: t('tasks.sort.dueDate') },
                    { value: 'estimate:desc', label: t('tasks.sort.estimate') },
                  ]}
                  className="w-full sm:w-44"
                />
              )}
            </div>
          )}
        </div>
      </div>

      <div className="mt-6">
        {(isLoading || statusesLoading) && <SkeletonTable rows={8} columns={6} />}
        {isError && <p className="text-red-600">{t('tasks.error.list')}</p>}
        {!isLoading && !statusesLoading && !isError && tasks.length === 0 && (
          <TasksEmptyState
            hasFilters={hasFilters}
            canQuickAdd={Boolean(effectiveProjectId)}
            onCreate={() => setCreating(true)}
          />
        )}

        {view === 'board' && !statusesLoading && tasks.length > 0 && (
          <FadeIn>
            <KanbanBoard
              tasks={tasks}
              onCardClick={openTask}
              projectId={effectiveProjectId ?? null}
              showProjectName={showProjectName}
            />
          </FadeIn>
        )}

        {view === 'list' && !statusesLoading && tasks.length > 0 && (
          <FadeIn>
            <ToastProvider>
              <LinearTaskList
                tasks={tasks}
                selected={selected}
                onToggleSelect={toggleSelect}
                onOpen={openTask}
                onLabelClick={handleLabelFilter}
                projectId={effectiveProjectId ?? null}
                showProjectName={showProjectName}
              />
            </ToastProvider>
          </FadeIn>
        )}

        {view === 'list' && pagination && pagination.totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
            <span>
              {t('tasks.pageInfo', {
                page: pagination.page,
                totalPages: pagination.totalPages,
                total: pagination.total,
              })}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={pagination.page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
              >
                {t('tasks.prev')}
              </button>
              <button
                type="button"
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
              >
                {t('tasks.next')}
              </button>
            </div>
          </div>
        )}
      </div>

      {creating && (
        <div className="ui-modal-backdrop" onClick={() => setCreating(false)}>
          <div className="ui-modal-panel w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <h2 className="mb-3 text-lg font-semibold text-gray-900">{t('tasks.createTitle')}</h2>
            <TaskForm
              isSubmitting={create.isPending}
              submitLabel={t('tasks.createSubmit')}
              defaultProjectId={lockedProjectId ?? createProjectId}
              lockProject={Boolean(lockedProjectId)}
              onCancel={() => setCreating(false)}
              onSubmit={handleCreate}
            />
            {create.isError && (
              <p className="mt-2 text-sm text-red-600">{t('tasks.createError')}</p>
            )}
          </div>
        </div>
      )}

      {managingLabels && <LabelsManager onClose={() => setManagingLabels(false)} />}

      {applyingTemplate && lockedProjectId && (
        <ApplyTemplateModal
          projectId={lockedProjectId}
          onClose={() => setApplyingTemplate(false)}
          onApplied={(count) => setTemplateNotice(t('tasks.applyTemplate.applied', { count }))}
        />
      )}

      {/* Bulk actions toolbar (FR-TASK #7) — only meaningful in list view. */}
      {view === 'list' && (
        <BulkActionsBar selectedIds={[...selected]} onClear={() => setSelected(new Set())} />
      )}
    </div>
  );
}

// Distinguish "no tasks at all" from "filters hide everything" so an empty
// list is never an unexplained dead end (CLE-197 edge case).
function TasksEmptyState({
  hasFilters,
  canQuickAdd,
  onCreate,
}: {
  hasFilters: boolean;
  canQuickAdd: boolean;
  onCreate: () => void;
}) {
  const { t } = useI18n();
  if (hasFilters) {
    return <p className="text-gray-500">{t('tasks.empty.filtered')}</p>;
  }
  return (
    <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-8 text-center">
      <p className="text-sm font-medium text-gray-700">{t('tasks.empty.title')}</p>
      <p className="mx-auto mt-1 max-w-md text-sm text-gray-500">
        {canQuickAdd ? t('tasks.empty.hintProject') : t('tasks.empty.hintGlobal')}
      </p>
      <button
        type="button"
        onClick={onCreate}
        className="mt-4 inline-flex items-center justify-center gap-1.5 rounded bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
      >
        <Plus size={16} /> {t('tasks.empty.cta')}
      </button>
    </div>
  );
}
